import React from 'react';
import { RED, INK, TEAL, GREY, LIGHT, SOFT, BORDER, FONT, wrap, btnRed, Crumbs } from '../seo/SeoUI';

const Stars = ({ value }) => {
  const v = Math.round(Number(value) || 0);
  return <span style={{ color: '#F5A623', letterSpacing: 1 }}>{'★★★★★'.slice(0, v)}<span style={{ color: '#E0E0E0' }}>{'★★★★★'.slice(v)}</span></span>;
};

function ProviderProfile({ pro }) {
  const name = `${pro.firstName || 'Pro'} ${pro.lastName || ''}`.trim();
  const initials = `${(pro.firstName || 'P')[0] || ''}${(pro.lastName || '')[0] || ''}`.toUpperCase();
  const avatar = pro.picture ? `/images/avatar/${pro.picture}` : null;
  const services = pro.services || [];
  const reviews = pro.reviews || [];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name,
    image: avatar || undefined,
    makesOffer: services.map((s) => ({ '@type': 'Offer', itemOffered: { '@type': 'Service', name: s } })),
    ...(pro.reviewCount ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: pro.avgRating, reviewCount: pro.reviewCount } } : {}),
    review: reviews.slice(0, 10).map((r) => ({ '@type': 'Review', reviewRating: { '@type': 'Rating', ratingValue: r.rating }, author: { '@type': 'Person', name: r.authorName }, reviewBody: r.content })),
  };

  return (
    <div style={{ fontFamily: FONT, color: INK, background: '#fff' }}>
      {/* SECURITY: escape </script> breakouts — review text/names are user-controlled (stored XSS vector). */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026') }} />

      {/* Header */}
      <section style={{ background: `radial-gradient(1000px 300px at 80% -10%, ${SOFT} 0%, rgba(255,246,244,0) 60%), linear-gradient(180deg,#fff 0%, ${LIGHT} 100%)`, borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ ...wrap, padding: '40px 20px 44px' }}>
          <Crumbs items={[{ label: 'Home', href: '/' }, { label: 'Pros' }, { label: name }]} />
          <div style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ width: 96, height: 96, borderRadius: 999, overflow: 'hidden', background: '#fff', border: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 32, color: RED, flex: '0 0 auto' }}>
              {avatar ? <img src={avatar} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
            </div>
            <div>
              <h1 style={{ fontSize: 34, fontWeight: 800, margin: '0 0 8px' }}>{name}</h1>
              <div style={{ fontSize: 15, color: GREY }}>
                {pro.reviewCount > 0
                  ? <span><Stars value={pro.avgRating} /> <b style={{ color: INK }}>{pro.avgRating}</b> · {pro.reviewCount} review{pro.reviewCount > 1 ? 's' : ''}</span>
                  : <span style={{ color: TEAL, fontWeight: 600 }}>✓ Verified pro</span>}
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {services.map((s) => (
                  <span key={s} style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 999, padding: '5px 12px', fontSize: 13, fontWeight: 600 }}>{s}</span>
                ))}
              </div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch' }}>
              {/* Web path: book this pro directly (login prompted at checkout). Carries
                  the pro + the shared service (?service=<categoryId>) into the flow. */}
              <a href="/category?type=all" style={btnRed}
                 onClick={() => {
                   try {
                     if (typeof window === 'undefined') return;
                     if (pro.id) window.localStorage.setItem('bh_preferred_pro', pro.id);
                     const svc = new URLSearchParams(window.location.search).get('service');
                     if (svc) window.localStorage.setItem('bh_preferred_service', svc);
                   } catch (e) { /* ignore */ }
                 }}>Book this pro</a>
              {/* No app? Send them to the right store (device-aware redirect in
                  /get-app), carrying pro + service so Android deep-links back. */}
              <a href={`/get-app?pro=${encodeURIComponent(pro.id || '')}`} style={{ ...btnRed, background: '#fff', color: RED, border: `1px solid ${RED}` }}
                 onClick={(e) => {
                   try {
                     if (typeof window === 'undefined') return;
                     const svc = new URLSearchParams(window.location.search).get('service');
                     if (svc) {
                       e.preventDefault();
                       window.location.href = `/get-app?pro=${encodeURIComponent(pro.id || '')}&service=${encodeURIComponent(svc)}`;
                     }
                   } catch (err) { /* ignore → href fallback */ }
                 }}>Get the app</a>
            </div>
          </div>
          <div style={{ marginTop: 18, display: 'flex', flexWrap: 'wrap', gap: '8px 20px', fontSize: 13, fontWeight: 600 }}>
            {/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */}
            <span><span style={{ color: TEAL, fontWeight: 800 }}>✓</span> Identity verified</span>
            <span><span style={{ color: TEAL, fontWeight: 800 }}>✓</span> Secure in-app payment</span>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section style={{ ...wrap, padding: '40px 20px 60px' }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 18px' }}>Reviews</h2>
        {reviews.length === 0 ? (
          <div style={{ background: LIGHT, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '24px', color: GREY }}>
            No reviews yet — be the first to book {pro.firstName || 'this pro'} and leave a review.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            {reviews.map((r) => (
              <div key={r.id} style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <b style={{ fontSize: 15 }}>{r.authorName}</b>
                  <span style={{ fontSize: 12, color: GREY }}>{r.createdAt}</span>
                </div>
                <div style={{ marginBottom: 6 }}><Stars value={r.rating} /></div>
                <p style={{ margin: 0, fontSize: 15, color: '#374151', lineHeight: 1.6 }}>{r.content}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default ProviderProfile;
